import { useState } from "react";
import { useLocation } from "react-router-dom";
import { FileText, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function QuotePage() {
  const { user } = useAuth();
  const initialItem = useLocation().state?.item;
  const [items, setItems] = useState(initialItem ? [initialItem] : [{ sku: "", name: "", qty: 1 }]);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    company_name: user?.company_name || "", reg_number: user?.reg_number || "", message: "",
  });
  const [sent, setSent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setItem = (i, patch) => setItems((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Lūdzu, aizpildiet kontaktinformāciju"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes", {
        ...form,
        items: items.filter((i) => i.name.trim()).map((i) => ({ sku: i.sku || null, name: i.name, qty: i.qty })),
      });
      setSent(data.number);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <FileText size={56} className="mx-auto text-[#16A34A]" />
        <h1 data-testid="quote-success-title" className="mt-6 font-heading text-3xl font-black uppercase tracking-tighter">Pieprasījums nosūtīts!</h1>
        <p className="mt-3 text-slate-600">Demo pieprasījuma numurs: <span className="font-bold">{sent}</span></p>
        <p className="mt-2 text-sm text-slate-500">Pieprasījums saglabāts tikai šajā pārlūkā un nav nosūtīts DANS G komandai.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Piedāvājuma pieprasījums</h1>
      <p className="mt-2 text-sm text-slate-600 max-w-2xl">Nepieciešams liels apjoms — eļļa mucās, AdBlue konteineros vai regulāras piegādes autoparkam? Aizpildiet formu, un sagatavosim individuālu cenu piedāvājumu ar apjoma atlaidēm.</p>
      <p className="mt-3 border border-blue-200 bg-blue-50 p-3 text-sm text-slate-600" data-testid="quote-demo-notice">Demo versija — forma netiks nosūtīta; ievadītie dati paliks tikai šajā pārlūkā.</p>

      <form onSubmit={submit} className="mt-6 space-y-8">
        <section className="rounded-2xl border border-border p-6">
          <h2 className="font-heading text-lg font-bold uppercase">Kontaktinformācija</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <input data-testid="quote-name" placeholder="Vārds, uzvārds *" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="quote-email" type="email" placeholder="E-pasts *" value={form.email} onChange={set("email")} className={inputCls} />
            <input data-testid="quote-phone" placeholder="Tālrunis *" value={form.phone} onChange={set("phone")} className={inputCls} />
            <input data-testid="quote-company" placeholder="Uzņēmums / saimniecība" value={form.company_name} onChange={set("company_name")} className={inputCls} />
            <input data-testid="quote-regnr" placeholder="Reģ. numurs" value={form.reg_number} onChange={set("reg_number")} className={inputCls} />
          </div>
        </section>

        <section className="rounded-2xl border border-border p-6">
          <h2 className="font-heading text-lg font-bold uppercase">Nepieciešamie produkti</h2>
          <div className="mt-4 space-y-3">
            {items.map((r, i) => (
              <div key={i} className="grid grid-cols-[110px_1fr_80px_36px] gap-3 items-center">
                <input data-testid={`quote-item-sku-${i}`} placeholder="Artikuls" value={r.sku || ""} onChange={(e) => setItem(i, { sku: e.target.value })} className={`${inputCls} font-mono text-xs`} />
                <input data-testid={`quote-item-name-${i}`} placeholder="Produkta nosaukums / apraksts" value={r.name} onChange={(e) => setItem(i, { name: e.target.value })} className={inputCls} />
                <input data-testid={`quote-item-qty-${i}`} type="number" min="1" value={r.qty} onChange={(e) => setItem(i, { qty: Math.max(1, parseInt(e.target.value) || 1) })} className={`${inputCls} text-center`} />
                <button type="button" aria-label="Dzēst" onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-[#2E86D1]"><Trash size={16} /></button>
              </div>
            ))}
          </div>
          <button type="button" data-testid="quote-add-item-btn" onClick={() => setItems((p) => [...p, { sku: "", name: "", qty: 1 }])}
            className="btn-industrial mt-4 inline-flex items-center gap-1.5 border border-slate-300 px-4 py-2 text-xs font-semibold hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
            <Plus size={14} /> Pievienot produktu
          </button>
        </section>

        <section className="rounded-2xl border border-border p-6">
          <h2 className="font-heading text-lg font-bold uppercase">Papildu informācija</h2>
          <textarea data-testid="quote-message" placeholder="Piem.: nepieciešama regulāra motoreļļas piegāde saimniecībai reizi mēnesī, aptuveni 400L..." value={form.message} onChange={set("message")} rows={4} className={`${inputCls} mt-4`} />
        </section>

        <button data-testid="quote-submit-btn" type="submit" disabled={submitting}
          className="btn-industrial w-full md:w-auto bg-[#2E86D1] px-10 py-3.5 text-sm font-semibold tracking-wide text-white hover:bg-[#1F6DB2] disabled:opacity-50">
          {submitting ? "Nosūta..." : "Nosūtīt pieprasījumu"}
        </button>
      </form>
    </div>
  );
}
